import {
  ActionCard,
  ClientView,
  KycHelper,
  Loader,
  Sidebar,
} from "@/components";
import React, { Fragment, useEffect, useState } from "react";
import Exchange from "./Exchange";
import Transactions from "./Transactions";
import Settings from "./Settings";
import Inbox from "./Inbox";
import MyDashboard from "./MyDashboard";
import Clients from "./Clients";
import Clude from "./Clude";
import Reports from "./Reports";
import Groups from "./Groups";
import { RootState, useAppDispatch } from "@/store";
import {
  ComponentType,
  setActiveComponent,
  setActiveTab,
} from "@/store/slices/componentSlice";
import { useSelector } from "react-redux";
import { getData, getUser, saveData } from "@/utils/localStorage";
import GenericApi from "@/api/genericApi";
import {
  BankAccountQueryKey,
  ContentTypeQueryKey,
  CoPQueryKey,
  GenericStatusQueryKey,
  GetTransactionsQueryKey,
} from "@/config/constants/QueryKeys";
import { ApiRoutes } from "@/config/routes/ApiRoutes";
import { ApiError, ApiResponse } from "@/types/Api";
import { BankAccount } from "@/types/BankSettings";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ListContentType } from "@/types/ContentTypes";
import { checkPermissionByKey, isBitramp } from "@/utils/utils.tsx";
import { selectClient } from "@/store/slices/clientSlice.tsx";
import { Transaction } from "@/types/Transaction";
import { TransactionStatus } from "@/types/Status";
import { AuthResponse } from "@/types/Auth.tsx";
import { authStorageUser } from "@/config/constants/StorageKeys.tsx";
import { User } from "@/types/User.tsx";
import { ValidationErrors } from "@/types/ValidationError.tsx";
import CustomLogger from "@/utils/CustomLogger.tsx";
import { KycInterface, KycRequest } from "@/types/Kyc.tsx";
import { useSnackBarAlert } from "@/hooks/useSnackbar.tsx";
import { setInboxTransaction, setInboxUser } from "@/store/slices/inboxSlice";
import { setClientTransfer } from "@/store/slices/clientTransactionSlice";
import { GeneralStatus } from "@/types/BusinessInfoNEW";
import POSClients from "./POSClients";
import { LogsPage, VeriffPage } from "@/pages/index.tsx";
import { useNavigate } from "react-router-dom";

const LayoutETS: React.FC = () => {
  const { showSnackBar } = useSnackBarAlert();
  const { selectedClient } = useSelector((state: RootState) => state.client);
  const localUser = getUser();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const activeComponent = useSelector(
    (state: RootState) => state.component.activeComponent
  );

  const [closeKYC, setCloseKYC] = useState(false);
  const [useVeriff] = useState(true);

  const { data: currentUser } = useQuery<ApiResponse<User>>({
    queryFn: async () =>
      GenericApi.get<User>(
        `${ApiRoutes.USERS}/${
          selectedClient ? selectedClient.id : localUser?.id
        }`,
        undefined,
        (data) => {
          const loginData = getData<AuthResponse>(authStorageUser);

          saveData(authStorageUser, {
            ...loginData,
            user: {
              ...loginData?.user,
              natural_person: data.natural_person,
            },
          });
        }
      ),
    queryKey: [CoPQueryKey, selectedClient ? selectedClient.id : localUser?.id],
  });

  const user = currentUser?.data;

  const permissions = user?.permissions ?? [];

  const { data: transactions } = useQuery<ApiResponse<Transaction[]>>({
    queryFn: async () =>
      GenericApi.get<Transaction[]>(
        `${ApiRoutes.TRANSACTIONS}/user/${user?.id}`
      ),
    enabled: !!user?.id,
    queryKey: [GetTransactionsQueryKey],
  });

  const { data: userGeneralStatus } = useQuery<ApiResponse<GeneralStatus>>({
    queryFn: async () =>
      GenericApi.get<GeneralStatus>(`${ApiRoutes.GENERALSTATUS}`),
    queryKey: [GenericStatusQueryKey],
  });

  console.log("User General Status:", userGeneralStatus);

  const createKycAccessToken = useMutation<
    ApiResponse<KycInterface>,
    ApiError<ValidationErrors>,
    KycRequest
  >({
    mutationFn: (data) =>
      GenericApi.post<KycInterface>(ApiRoutes.KYC_TOKEN, data),
    onSuccess: ({ data }) => {
      CustomLogger.log(data);
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      showSnackBar(
        "An error occurred",
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000
      );
    },
  });

  const completedTransactions =
    transactions?.data?.filter(
      (transaction) => transaction.status === TransactionStatus.COMPLETED
    ).length || 0;

  const otherTransactions =
    transactions?.data?.filter(
      (transaction) => transaction.status !== TransactionStatus.COMPLETED
    ).length || 0;

  const totalTransactions = transactions?.data?.length || 0;

  const { data: contentType } = useQuery<ApiResponse<ListContentType[]>>({
    queryFn: async () =>
      GenericApi.get<ListContentType[]>(ApiRoutes.CONTENTYPES),
    queryKey: [ContentTypeQueryKey],
  });

  const isSuperuser = getUser()?.is_staff;

  useEffect(() => {
    const themeClass = isBitramp() ? "theme-bitramp" : "theme-leonext";
    document.body.classList.add(themeClass);

    return () => {
      document.body.classList.remove("theme-bitramp");
    };
  }, []);

  useEffect(() => {
    dispatch(
      setActiveComponent(
        userGeneralStatus?.data?.status?.status == "approved"
          ? "Exchange"
          : "Settings"
      )
    );
  }, [dispatch]);
  const [loading, setLoading] = useState(false);

  const handleComponentChange = (component: ComponentType) => {
    dispatch(setActiveComponent(component));
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 700);
    dispatch(setInboxTransaction(null));
    dispatch(setInboxUser(null));
    dispatch(setClientTransfer(null));
  };

  const { data: bankAccounts } = useQuery<ApiResponse<BankAccount[]>>({
    queryKey: [BankAccountQueryKey],
    queryFn: async () => GenericApi.get<BankAccount[]>(ApiRoutes.BANKACCOUNT),
  });

  const hasBankAccount = bankAccounts?.data && bankAccounts?.data.length > 0;

  const permissionList = permissions.filter((perm) => perm.startsWith("view"));

  const canAccessTransactionClient =
    user?.is_staff === false &&
    userGeneralStatus?.data?.status?.status === "approved";

  const canAccessExchange = checkPermissionByKey(
    permissionList,
    "exchangerate"
  );
  const canAccessTransactions = checkPermissionByKey(
    permissionList,
    "transaction"
  );

  const handleGetRequest = async () => {
    try {
      const response = await GenericApi.post(`/kyc/create-applicant`, {});
      console.info("Request successful:", response);
    } catch (error) {
      console.error("Request failed with error:", error);
    }
  };

  const handleKycRequest = async () => {
    if (!currentUser?.data?.email || !currentUser?.data?.id) return;
    //TODO Check phone number

    createKycAccessToken.mutate({
      applicantIdentifiers: {
        email: currentUser?.data?.email,
        phone: "555-1111",
      },
      ttlInSecs: 600,
      userId: String(currentUser?.data?.id),
      levelName: "basic-kyc-level",
    });
  };

  const [intro, setIntro] = useState(false);
  useEffect(() => {
    if (
      userGeneralStatus?.data?.business_info?.status === "uninitiated" &&
      userGeneralStatus?.data?.individual_info?.status === "uninitiated" &&
      userGeneralStatus?.data?.financial_info?.status === "uninitiated" &&
      user?.client_info?.kyc_status === undefined &&
      !isSuperuser &&
      userGeneralStatus?.data?.status?.status !== "approved"
    ) {
      setIntro(true);
    }
  }, [userGeneralStatus]);

  return (
    <div className="dashboard min-h-screen">
      <Sidebar
        onSelect={handleComponentChange}
        activeComponent={activeComponent}
        permissionsList={permissionList}
        allPermissions={contentType?.data ?? null}
      />
      <div className="dashboard__right">
        {!isSuperuser ? (
          <div className="dashboard__right--flex">
            {userGeneralStatus?.data?.status?.status != "approved" ? (
              <div className="h-full flex justify-between items-center w-full pr-12">
                <ActionCard
                  buttonText="Upload"
                  actionCallback={() => {
                    dispatch(setActiveTab(0));
                  }}
                  subtitle=""
                  title={
                    <p>
                      Business <br /> Info
                    </p>
                  }
                  style={{
                    opacity:
                      userGeneralStatus?.data?.business_info.status == "pending"
                        ? 0.3
                        : 1,
                  }}
                  revStyle={
                    userGeneralStatus?.data?.business_info.status ==
                      "pending" ||
                    userGeneralStatus?.data?.business_info.status == "approved"
                  }
                  style_step={
                    userGeneralStatus?.data?.business_info.status !==
                    "uninitiated"
                      ? isBitramp()
                        ? "bg-[linear-gradient(to_right,#8AFEFD,#EE33D9)]"
                        : "active"
                      : isBitramp()
                      ? ""
                      : ""
                  }
                  status={userGeneralStatus?.data?.business_info.status}
                  isDisabled={
                    userGeneralStatus?.data?.business_info.status ==
                      "pending" ||
                    userGeneralStatus?.data?.business_info.status == "approved"
                  }
                />
                <ActionCard
                  buttonText="Ready"
                  actionCallback={() => {
                    dispatch(setActiveTab(1));
                  }}
                  subtitle="Welcome to Leonext"
                  title="Individual"
                  style={{
                    opacity:
                      userGeneralStatus?.data?.individual_info.status ==
                      "pending"
                        ? 0.3
                        : 1,
                  }}
                  revStyle={
                    userGeneralStatus?.data?.individual_info.status ==
                      "pending" ||
                    userGeneralStatus?.data?.individual_info.status ==
                      "approved"
                  }
                  style_step={
                    userGeneralStatus?.data?.individual_info.status !==
                    "uninitiated"
                      ? isBitramp()
                        ? "bg-[linear-gradient(to_right,#8AFEFD,#EE33D9)]"
                        : "active"
                      : isBitramp()
                      ? ""
                      : ""
                  }
                  status={userGeneralStatus?.data?.individual_info.status}
                  isDisabled={
                    userGeneralStatus?.data?.individual_info.status ==
                      "pending" ||
                    userGeneralStatus?.data?.individual_info.status ==
                      "approved"
                  }
                />
                <ActionCard
                  buttonText="Verify"
                  actionCallback={() => {
                    dispatch(setActiveTab(2));
                  }}
                  subtitle="Sign and verify"
                  title="Questionary"
                  style={{
                    opacity:
                      userGeneralStatus?.data?.financial_info.status ==
                      "pending"
                        ? 0.3
                        : 1,
                  }}
                  revStyle={
                    userGeneralStatus?.data?.financial_info.status ==
                      "pending" ||
                    userGeneralStatus?.data?.financial_info.status == "approved"
                  }
                  style_step={
                    userGeneralStatus?.data?.financial_info.status !=
                    "uninitiated"
                      ? "active"
                      : ""
                  }
                  status={userGeneralStatus?.data?.financial_info.status}
                  isDisabled={
                    userGeneralStatus?.data?.financial_info.status ==
                      "pending" ||
                    userGeneralStatus?.data?.financial_info.status == "approved"
                  }
                />
                <ActionCard
                  buttonText="Begin"
                  actionCallback={() => {
                    handleKycRequest(), setCloseKYC(true), handleGetRequest();
                  }}
                  subtitle="KYC process"
                  title="Verify Identity"
                  style={{
                    opacity:
                      user?.client_info?.kyc_status != "uninitiated" &&
                      user?.client_info != null
                        ? 0.3
                        : 1,
                  }}
                  revStyle={
                    user?.client_info?.kyc_status != "uninitiated" &&
                    user?.client_info != null
                  }
                  style_step={
                    user?.client_info?.kyc_status != "uninitiated" &&
                    user?.client_info != null
                      ? isBitramp()
                        ? "bg-[linear-gradient(to_right,#8AFEFD,#EE33D9)]"
                        : "active"
                      : isBitramp()
                      ? ""
                      : ""
                  }
                  status={
                    user?.client_info?.kyc_status != "uninitiated" &&
                    user?.client_info != null
                      ? user?.client_info?.kyc_status
                      : "Not completed"
                  }
                  isDisabled={user?.client_info?.kyc_status == "approved"}
                />
                <ActionCard
                  buttonText="Add"
                  actionCallback={() => {
                    dispatch(setActiveTab(3));
                  }}
                  subtitle="Bank account or wallet"
                  title="Transfer Details"
                  style={{ opacity: hasBankAccount ? 0.3 : 1 }}
                  revStyle={
                    !!(
                      user?.client_info?.kyc_status == "pending" &&
                      hasBankAccount
                    )
                  }
                  style_step={
                    hasBankAccount
                      ? isBitramp()
                        ? "bg-[linear-gradient(to_right,#8AFEFD,#EE33D9)]"
                        : "active"
                      : isBitramp()
                      ? ""
                      : ""
                  }
                  status={
                    bankAccounts?.data && bankAccounts?.data.length > 0
                      ? "Added"
                      : "Not completed"
                  }
                  isDisabled={false}
                />
              </div>
            ) : (
              <ClientView
                transactions={totalTransactions}
                completed={completedTransactions}
                incompleted={otherTransactions}
              />
            )}
          </div>
        ) : (
          <div className="w-full justify-center flex items-center">
            {selectedClient ? (
              <div
                className={`${
                  isBitramp()
                    ? "text-[#fff] bg-[#EE33D9]"
                    : "bg-[#222b45] text-white"
                }  flex gap-5 justify-between items-center p-3  rounded-lg`}
              >
                <p>
                  {selectedClient?.first_name} {selectedClient?.last_name}
                </p>
                <button
                  onClick={() => {
                    dispatch(selectClient(null));
                    dispatch(setActiveComponent("Clients"));
                    window.location.reload();
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="white"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            ) : null}
          </div>
        )}
        <div className="w-full flex items-start justify-between">
          <div className="w-[93%]">
            {loading ? (
              <Loader />
            ) : (
              <>
                {activeComponent === "Exchange" &&
                (canAccessExchange || isSuperuser) ? (
                  <Exchange />
                ) : null}
                {activeComponent === "Transactions" &&
                (canAccessTransactions ||
                  isSuperuser ||
                  canAccessTransactionClient) ? (
                  <Transactions />
                ) : null}
                {activeComponent === "POS Clients" &&
                (canAccessTransactions ||
                  isSuperuser ||
                  canAccessTransactionClient) ? (
                  <POSClients />
                ) : null}
                {activeComponent === "Settings" && <Settings />}
                {activeComponent === "Inbox" && <Inbox />}
                {activeComponent === "Dashboard" && <MyDashboard />}
                {activeComponent === "Clude" && <Clude />}
                {activeComponent === "Reports" && <Reports />}
                {activeComponent === "Clients" && <Clients />}
                {activeComponent === "Groups" && <Groups />}
                {activeComponent === "Logs" && <LogsPage />}
              </>
            )}
          </div>
          {isBitramp() ? (
            <div className="flex flex-col items-center w-[4%] Text semi-bold fixed right-0 top-1/2 -translate-y-1/2">
              <div
                className="origin-top w-full flex justify-center items-center text-[#fff] absolute h-fit -top-[4.5rem] -right-2 overflow-hidden"
                style={{ writingMode: "vertical-rl" }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="65"
                  height="162"
                  viewBox="0 0 65 162"
                  fill="none"
                >
                  <g filter="url(#filter0_d_4903_14514)">
                    <path
                      d="M6.21265 23.7654C6.21839 19.5569 9.13983 15.9155 13.2467 14.998L55.2476 5.61404L55.106 135.614L12.4599 122.566C8.67067 121.406 6.08432 117.906 6.08972 113.944L6.21265 23.7654Z"
                      fill="#2B2B2B"
                    />
                  </g>
                  <defs>
                    <filter
                      id="filter0_d_4903_14514"
                      x="0.689844"
                      y="0.214258"
                      width="79.9577"
                      height="160.8"
                      filterUnits="userSpaceOnUse"
                      color-interpolation-filters="sRGB"
                    >
                      <feFlood flood-opacity="0" result="BackgroundImageFix" />
                      <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                      />
                      <feOffset dx="10" dy="10" />
                      <feGaussianBlur stdDeviation="7.7" />
                      <feComposite in2="hardAlpha" operator="out" />
                      <feColorMatrix
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.03 0"
                      />
                      <feBlend
                        mode="normal"
                        in2="BackgroundImageFix"
                        result="effect1_dropShadow_4903_14514"
                      />
                      <feBlend
                        mode="normal"
                        in="SourceGraphic"
                        in2="effect1_dropShadow_4903_14514"
                        result="shape"
                      />
                    </filter>
                  </defs>
                </svg>

                <div
                  onClick={() => navigate("/crmSoon")}
                  className="absolute cursor-pointer top-10 flex flex-col font-bold text-white pt-[0.4rem]"
                >
                  <div>CRM</div>
                </div>
              </div>
              <div
                className="rotate-270 origin-top w-full ml-[0.2rem] py-7 flex justify-center items-center text-[#000] z-[9]"
                style={{ writingMode: "vertical-rl" }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="58"
                  height="175"
                  viewBox="0 0 65 175"
                  fill="none"
                >
                  <path
                    d="M0.199469 18.9267C0.205123 14.5264 3.39181 10.7767 7.7334 10.0617L65.1059 0.613811L64.9098 174.114L7.63693 165.163C3.2501 164.477 0.0172863 160.695 0.0229917 156.255L0.199469 18.9267Z"
                    fill="#8AFEFD"
                  />
                </svg>
                <div
                  onClick={() => navigate("/dashboard")}
                  className="cursor-pointer absolute text-[1.2rem] font-[700]"
                >
                  Platform
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center w-[4%] Text semi-bold fixed right-0 top-1/2 -translate-y-1/2">
              <div
                className="origin-top w-full flex justify-center items-center text-[#fff] absolute h-fit -top-[4.5rem] -right-2 overflow-hidden"
                style={{ writingMode: "vertical-rl" }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="65"
                  height="162"
                  viewBox="0 0 65 162"
                  fill="none"
                >
                  <g filter="url(#filter0_d_4903_14514)">
                    <path
                      d="M6.21265 23.7654C6.21839 19.5569 9.13983 15.9155 13.2467 14.998L55.2476 5.61404L55.106 135.614L12.4599 122.566C8.67067 121.406 6.08432 117.906 6.08972 113.944L6.21265 23.7654Z"
                      fill="#fff"
                    />
                  </g>
                  <defs>
                    <filter
                      id="filter0_d_4903_14514"
                      x="0.689844"
                      y="0.214258"
                      width="79.9577"
                      height="160.8"
                      filterUnits="userSpaceOnUse"
                      color-interpolation-filters="sRGB"
                    >
                      <feFlood flood-opacity="0" result="BackgroundImageFix" />
                      <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                      />
                      <feOffset dx="10" dy="10" />
                      <feGaussianBlur stdDeviation="7.7" />
                      <feComposite in2="hardAlpha" operator="out" />
                      <feColorMatrix
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.03 0"
                      />
                      <feBlend
                        mode="normal"
                        in2="BackgroundImageFix"
                        result="effect1_dropShadow_4903_14514"
                      />
                      <feBlend
                        mode="normal"
                        in="SourceGraphic"
                        in2="effect1_dropShadow_4903_14514"
                        result="shape"
                      />
                    </filter>
                  </defs>
                </svg>

                <div
                  // onClick={() => navigate("/crmSoon")}
                  className=" absolute top-10 flex flex-col font-bold text-[#202A43]"
                >
                  <div>CRM</div>
                </div>
              </div>
              <div
                className="ml-[0.2rem] rotate-270 origin-top w-full py-5 flex justify-center items-center text-[#fff] z-[9]"
                style={{ writingMode: "vertical-rl" }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="58"
                  height="175"
                  viewBox="0 0 65 175"
                  fill="none"
                >
                  <path
                    d="M0.199469 18.9267C0.205123 14.5264 3.39181 10.7767 7.7334 10.0617L65.1059 0.613811L64.9098 174.114L7.63693 165.163C3.2501 164.477 0.0172863 160.695 0.0229917 156.255L0.199469 18.9267Z"
                    fill="#202A43"
                  />
                </svg>
                <div
                  onClick={() => navigate("/dashboard")}
                  className=" absolute text-[1.2rem] font-[700]"
                >
                  Platform
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {closeKYC ? (
        <div
          onClick={() => setCloseKYC(false)}
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[999999999] h-screen min-h-full"
        >
          {!useVeriff ? (
            <Fragment>
              <div
                onClick={(e) => e.stopPropagation()}
                className="p-[2rem] max-h-[90%] w-[50%] overflow-y-scroll bg-white rounded-lg flex flex-col items-center"
              >
                {createKycAccessToken?.isPending ? (
                  <div>
                    <h1>Loading ...</h1>
                  </div>
                ) : createKycAccessToken?.isSuccess ? (
                  <div className="w-full">
                    <KycHelper
                      accessToken={
                        createKycAccessToken?.data?.data?.token ?? ""
                      }
                    />
                  </div>
                ) : null}
                <div
                  className="text-white bg-red-600 w-[70%] px-4 py-2 rounded-lg text-[1rem] text-center cursor-pointer"
                  onClick={() => setCloseKYC(false)}
                >
                  Close
                </div>
              </div>
            </Fragment>
          ) : (
            <div
              onClick={(e) => e.stopPropagation()}
              className="p-[2rem] max-h-[90%] w-[50%] overflow-y-scroll bg-white rounded-lg flex flex-col items-center"
            >
              <VeriffPage />
              <div
                className="text-white bg-red-600 w-[70%] px-4 py-2 rounded-lg text-[1rem] text-center cursor-pointer"
                onClick={() => setCloseKYC(false)}
              >
                Close
              </div>
            </div>
          )}
        </div>
      ) : null}
      {intro ? (
        <div
          onClick={() => setIntro(false)}
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[999999999] h-screen min-h-full"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`p-[2rem] max-h-[90%] w-[50%]  rounded-lg flex flex-col items-center justify-center  ${
              isBitramp()
                ? "text-white bg-[#2B2B2B]"
                : "bg-white text-[#05004e]"
            }`}
          >
            <h1 className="text-[1.5rem] font-bold mb-12 text-center">
              Verify Your Account to Unlock Full Access
            </h1>
            <p className="mb-12 text-center">
              Welcome aboard! To ensure the safety of your funds and comply with
              regulations, please complete your identity verification (KYC).
              <br />
              <br />
              It only takes a few minutes and unlocks full access to trading and
              platform features.
            </p>
            <div className="Primary_button" onClick={() => setIntro(false)}>
              Close
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default LayoutETS;
